// Admin drill-down views: user → vehicles → recording → analysis runner.
//
// Wired into AdminDashboard via a `view` state machine. Each component
// fetches what it needs on mount and provides a "back" handler for the
// breadcrumb in the parent.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api, AdminVehicle, AdminVehicleListResponse,
  AdminVehicleRecording, AdminVehicleRecordingsResponse,
  AdminRecordingMetadata, AnalysisResult, UnmixMethod, ComponentsResult,
  RecordingLabels, AnalysisSchemaStatus,
  ClassifierStatus, ClassifierTrainResult,
} from './api';
import { CalibrateCylindersModal } from './CalibrateCylindersModal';
import { CylinderEnsembleView } from './CylinderEnsembleView';
import {
  LiveRmsChart, LiveSpectrogramChart, LiveComponentsChart,
  TimeAxis, FreqAxis,
} from './RmsChart';
import { morletCWT, logScales, WAVELET_NAME } from './wavelet';
import { playLoopedClip, PlayHandle } from './audio';
import { DiagnosticModal } from './DiagnosticModal';
import { TrendsModal } from './TrendsModal';

function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(2)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
function fmtTs(ms: number): string {
  return new Date(ms).toLocaleString();
}

/** Color-coded pill describing whether a recording's persisted
 *  analysis_json reflects the current pipeline schema. Drives
 *  decisions about which recordings need to be re-analyzed (with
 *  Save = on) to backfill the cycle-fingerprint pipeline output. */
export function SchemaBadge({ status }: { status: AnalysisSchemaStatus | undefined }) {
  if (!status) return <span className="muted" style={{ fontSize: '0.78rem' }}>—</span>;
  const presets: Record<AnalysisSchemaStatus, { label: string; color: string }> = {
    current: { label: 'current', color: 'var(--ok)' },
    stale:   { label: 'stale',   color: '#f59e0b' },
    none:    { label: 'no analysis', color: 'var(--muted)' },
  };
  const p = presets[status];
  return (
    <span
      title={
        status === 'current'
          ? 'Analysis JSON includes the latest cycle-fingerprint fields.'
          : status === 'stale'
            ? 'Analyzed under an older pipeline. Open and run analysis with Save on to backfill.'
            : 'No successful analysis yet.'
      }
      style={{
        display: 'inline-block',
        padding: '1px 7px', borderRadius: 999,
        border: `1px solid ${p.color}`,
        color: p.color, fontSize: '0.7rem', fontWeight: 700,
        letterSpacing: '0.04em', textTransform: 'uppercase',
      }}
    >{p.label}</span>
  );
}

// ---- User view: vehicles owned by the user -----------------------------

interface UserViewProps {
  userId: number;
  onBack: () => void;
  onOpenVehicle: (vehicleId: number) => void;
}

export function UserView({ userId, onBack, onOpenVehicle }: UserViewProps) {
  const [data, setData] = useState<AdminVehicleListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<AdminVehicleListResponse>(`/api/admin/users/${userId}/vehicles`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); });
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <>
      <div className="panel">
        <button className="secondary" onClick={onBack}>← Back to users</button>
        <h3 style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          {data ? data.user.username : `user #${userId}`}
        </h3>
        {data && (
          <div className="muted" style={{ fontSize: '0.85rem' }}>
            joined {fmtTs(data.user.created_at)}
          </div>
        )}
      </div>
      {error && <div className="panel error">{error}</div>}
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Vehicles</h3>
        <table>
          <thead>
            <tr>
              <th>ID</th><th>Name</th><th>Engine</th><th>Created</th>
              <th>Recordings</th><th>Bytes used</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data?.vehicles.map((v: AdminVehicle) => (
              <tr key={v.id}>
                <td>{v.id}</td>
                <td>{v.name}</td>
                <td className="muted">
                  {v.num_cylinders != null
                    ? `${v.num_cylinders} cyl, ${v.num_strokes ?? '?'}-stroke` +
                      (v.num_valves_per_cylinder != null
                        ? `, ${v.num_valves_per_cylinder} v/cyl`
                        : '')
                    : '—'}
                </td>
                <td className="muted">{fmtTs(v.created_at)}</td>
                <td>{v.recording_count}</td>
                <td>{fmtBytes(v.bytes_used)}</td>
                <td>
                  <button className="secondary" onClick={() => onOpenVehicle(v.id)}>
                    View →
                  </button>
                </td>
              </tr>
            ))}
            {data && data.vehicles.length === 0 && (
              <tr><td colSpan={7} className="muted">no vehicles</td></tr>
            )}
            {!data && !error && (
              <tr><td colSpan={7} className="muted">loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- Vehicle view: recordings under a vehicle --------------------------

interface VehicleViewProps {
  vehicleId: number;
  onBack: () => void;
  onOpenRecording: (recordingId: number) => void;
}

export function VehicleView({ vehicleId, onBack, onOpenRecording }: VehicleViewProps) {
  const [data, setData] = useState<AdminVehicleRecordingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTrends, setShowTrends] = useState(false);
  const [showCalibrate, setShowCalibrate] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkSkipCurrent, setBulkSkipCurrent] = useState(true);
  const [bulkProgress, setBulkProgress] = useState<{
    done: number; total: number; current: number | null;
    failures: Array<{ id: number; error: string }>;
  } | null>(null);
  const bulkCancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  const refetch = useCallback(() => {
    api.get<AdminVehicleRecordingsResponse>(`/api/admin/vehicles/${vehicleId}/recordings`)
      .then((d) => setData(d))
      .catch((e) => setError((e as Error).message));
  }, [vehicleId]);

  useEffect(() => {
    let cancelled = false;
    api.get<AdminVehicleRecordingsResponse>(`/api/admin/vehicles/${vehicleId}/recordings`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); });
    return () => { cancelled = true; };
  }, [vehicleId]);

  // Selection helpers ---------------------------------------------------
  const allRecordings = data?.recordings ?? [];
  const selectableIds = useMemo(
    () => allRecordings
      .filter((r) => bulkSkipCurrent ? r.analysis_schema !== 'current' : true)
      .map((r) => r.id),
    [allRecordings, bulkSkipCurrent],
  );
  const allSelected = selectableIds.length > 0
    && selectableIds.every((id) => selected.has(id));
  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) return new Set();
      return new Set(selectableIds);
    });
  };
  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Bulk runner ---------------------------------------------------------
  // Sequential client-side loop. Each clip is its own atomic UPDATE
  // server-side, so a mid-flight cancel just leaves later clips
  // unchanged — observable via the schema badges that re-fetch when
  // the loop ends.
  const runBulk = useCallback(async () => {
    if (!data) return;
    const targets = allRecordings
      .filter((r) => selected.has(r.id))
      .filter((r) => bulkSkipCurrent ? r.analysis_schema !== 'current' : true);
    if (targets.length === 0) return;
    if (!confirm(
      `Run & save analysis on ${targets.length} recording${targets.length === 1 ? '' : 's'}?\n\n` +
      `This is sequential and can tie up the server's analysis pipeline ` +
      `for several minutes. Live recording analysis from users will queue ` +
      `behind it. Each clip's persisted analysis is overwritten with the ` +
      `current pipeline output.`,
    )) return;
    bulkCancelRef.current = { cancelled: false };
    setBulkRunning(true);
    setBulkProgress({ done: 0, total: targets.length, current: null, failures: [] });
    const failures: Array<{ id: number; error: string }> = [];
    const numCyl = data.vehicle.num_cylinders ?? 4;
    const numStrokes = data.vehicle.num_strokes ?? 4;
    for (let i = 0; i < targets.length; i += 1) {
      if (bulkCancelRef.current.cancelled) break;
      const r = targets[i];
      setBulkProgress({
        done: i, total: targets.length, current: r.id, failures: [...failures],
      });
      try {
        await api.post(
          `/api/admin/recordings/${r.id}/analyse`,
          {
            unmix_method: 'block',
            num_cylinders: numCyl,
            num_strokes: numStrokes,
            save: true,
            skipPrompts: true,
          },
        );
      } catch (e) {
        failures.push({ id: r.id, error: (e as Error).message });
      }
    }
    setBulkProgress({
      done: targets.length, total: targets.length, current: null, failures,
    });
    setBulkRunning(false);
    setSelected(new Set());
    refetch();
  }, [data, allRecordings, selected, bulkSkipCurrent, refetch]);

  const cancelBulk = () => { bulkCancelRef.current.cancelled = true; };

  // Vehicle data for the calibration modal — only mount with the
  // fields the modal needs (avoid passing unrelated AdminVehicle bits).
  const calibrationVehicle = data && data.vehicle.num_cylinders != null
    ? {
        id: data.vehicle.id,
        name: `${data.vehicle.username} / ${data.vehicle.name}`,
        num_cylinders: data.vehicle.num_cylinders,
        persistent_cyl_assignments: data.vehicle.persistent_cyl_assignments ?? null,
      }
    : null;

  return (
    <>
      <div className="panel">
        <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <button className="secondary" onClick={onBack}>← Back</button>
          <div className="row" style={{ gap: '0.4rem' }}>
            {calibrationVehicle && data?.vehicle.powertrain_kind !== 'ev' && (
              <button
                className="secondary"
                onClick={() => setShowCalibrate(true)}
                title="Map persistent IDs (P-A, P-B…) to physical cylinder numbers"
              >
                Calibrate cylinders
              </button>
            )}
            {data && (data.recordings?.length ?? 0) > 0 && (
              <button
                onClick={() => setShowTrends(true)}
                title="History of engine-health metrics across all recordings of this vehicle"
              >Trends</button>
            )}
          </div>
        </div>
        <h3 style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          {data ? `${data.vehicle.username} / ${data.vehicle.name}` : `vehicle #${vehicleId}`}
        </h3>
      </div>
      {error && <div className="panel error">{error}</div>}
      {showTrends && data && (
        <TrendsModal
          vehicleId={vehicleId}
          vehicleName={`${data.vehicle.username} / ${data.vehicle.name}`}
          fetchUrl={`/api/admin/vehicles/${vehicleId}/trends`}
          repairEventsUrl={`/api/admin/vehicles/${vehicleId}/repair-events`}
          onClose={() => setShowTrends(false)}
        />
      )}
      {showCalibrate && calibrationVehicle && (
        <CalibrateCylindersModal
          vehicle={calibrationVehicle}
          onClose={() => setShowCalibrate(false)}
          onSaved={(json) => {
            // Optimistically reflect the new mapping in the locally
            // cached vehicle row so a follow-up open of the modal
            // shows it without re-fetching.
            setData((prev) =>
              prev ? { ...prev, vehicle: { ...prev.vehicle, persistent_cyl_assignments: json } } : prev,
            );
          }}
        />
      )}
      <Phase3Panel vehicleId={vehicleId} />
      <div className="panel">
        <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Recordings</h3>
          <div className="row" style={{ gap: '0.4rem', alignItems: 'center' }}>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              margin: 0, fontSize: '0.8rem', color: 'var(--muted)',
            }}>
              <input
                type="checkbox"
                checked={bulkSkipCurrent}
                onChange={(e) => setBulkSkipCurrent(e.target.checked)}
                disabled={bulkRunning}
                style={{ width: 'auto' }}
              />
              Skip "current" schema
            </label>
            <button
              onClick={runBulk}
              disabled={bulkRunning || selected.size === 0}
              title={selected.size === 0
                ? 'Select recordings to bulk-analyze'
                : `Run & save on ${selected.size} selected`}
            >
              {bulkRunning
                ? `Processing ${(bulkProgress?.done ?? 0)}/${bulkProgress?.total ?? 0}…`
                : `Run & save on selected (${selected.size})`}
            </button>
            {bulkRunning && (
              <button className="secondary danger" onClick={cancelBulk}>Cancel</button>
            )}
          </div>
        </div>
        {bulkProgress && !bulkRunning && (
          <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
            Last bulk run: {bulkProgress.done}/{bulkProgress.total} processed
            {bulkProgress.failures.length > 0 && (
              <span style={{ color: 'var(--danger)' }}>
                {' · '}{bulkProgress.failures.length} failure{bulkProgress.failures.length === 1 ? '' : 's'}
                {' '}({bulkProgress.failures.slice(0, 5).map((f) => `#${f.id}`).join(', ')}
                {bulkProgress.failures.length > 5 && '…'})
              </span>
            )}
          </div>
        )}
        {bulkRunning && bulkProgress?.current != null && (
          <div className="muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
            Current: recording #{bulkProgress.current}
          </div>
        )}
        <table>
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={bulkRunning || selectableIds.length === 0}
                  title={allSelected ? 'Deselect all' : 'Select all eligible'}
                  style={{ width: 'auto' }}
                />
              </th>
              <th>ID</th><th>Created</th><th>Duration</th>
              <th>Sample rate</th><th>Size</th><th>Status</th>
              <th>Schema</th><th>Labels</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data?.recordings.map((r: AdminVehicleRecording) => {
              const isCurrent = r.analysis_schema === 'current';
              const eligible = bulkSkipCurrent ? !isCurrent : true;
              const isProcessing = bulkRunning && bulkProgress?.current === r.id;
              return (
                <tr key={r.id} style={isProcessing ? { background: 'rgba(245, 158, 11, 0.08)' } : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                      disabled={bulkRunning || !eligible}
                      title={!eligible ? 'Already current — toggle "Skip current schema" off to include' : ''}
                      style={{ width: 'auto' }}
                    />
                  </td>
                  <td>{r.id}</td>
                  <td className="muted">{fmtTs(r.created_at)}</td>
                  <td>{r.duration_sec.toFixed(2)}s</td>
                  <td>{r.sample_rate} Hz</td>
                  <td>{fmtBytes(r.size_bytes)}</td>
                  <td>{r.analysis_status}</td>
                  <td><SchemaBadge status={r.analysis_schema} /></td>
                  <td>
                    <LabelTags
                      labels={{
                        gauge_rpm: r.gauge_rpm ?? null,
                        powertrain: r.powertrain ?? null,
                        incident_keywords: r.incident_keywords ?? null,
                      }}
                    />
                  </td>
                  <td>
                    <button className="secondary" onClick={() => onOpenRecording(r.id)}>
                      Open →
                    </button>
                  </td>
                </tr>
              );
            })}
            {data && data.recordings.length === 0 && (
              <tr><td colSpan={10} className="muted">no recordings</td></tr>
            )}
            {!data && !error && (
              <tr><td colSpan={10} className="muted">loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- Recording view: metadata + analysis runner ------------------------

interface RecordingViewProps {
  recordingId: number;
  onBack: () => void;
}

export function RecordingView({ recordingId, onBack }: RecordingViewProps) {
  const [meta, setMeta] = useState<AdminRecordingMetadata | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);

  // Analysis runner state
  const [method, setMethod] = useState<UnmixMethod>('block');
  const [numCylinders, setNumCylinders] = useState<number>(4);
  const [numStrokes, setNumStrokes] = useState<2 | 4>(4);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [view, setView] = useState<'rms' | 'spectrogram' | 'components'>('rms');
  // Default ON: the most common reason to re-run analysis is to
  // backfill the persisted analysis_json with current-pipeline
  // output. Admin can uncheck for preview-only runs (e.g. comparing
  // unmix methods without overwriting the user's view).
  const [save, setSave] = useState(true);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<AdminRecordingMetadata>(`/api/admin/recordings/${recordingId}/metadata`)
      .then((m) => {
        if (cancelled) return;
        setMeta(m);
        // Seed runner inputs from sidecar (if present) so admin starts from
        // what the user actually had configured at recording time.
        if (m.engine) {
          setNumCylinders(m.engine.num_cylinders);
          setNumStrokes(m.engine.num_strokes === 2 ? 2 : 4);
        }
      })
      .catch((e) => { if (!cancelled) setMetaError((e as Error).message); });
    return () => { cancelled = true; };
  }, [recordingId]);

  const runAnalysis = useCallback(async () => {
    setRunning(true);
    setRunError(null);
    setResult(null);
    setSavedAt(null);
    try {
      const r = await api.post<AnalysisResult>(
        `/api/admin/recordings/${recordingId}/analyse`,
        {
          unmix_method: method,
          num_cylinders: numCylinders,
          num_strokes: numStrokes,
          save,
        },
      );
      setResult(r);
      if (save) setSavedAt(Date.now());
    } catch (e) {
      setRunError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }, [recordingId, method, numCylinders, numStrokes, save]);

  const compHeight = useMemo(() => {
    const n = result?.components.num_components ?? 0;
    return Math.max(120, 22 * n);
  }, [result]);

  const isIca = result?.components.method === 'ica';
  const isNmf = result?.components.method === 'nmf';
  const [showLabelEdit, setShowLabelEdit] = useState(false);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [baselineAnalysis, setBaselineAnalysis] = useState<AnalysisResult | null>(null);
  const [baselineRecordingId, setBaselineRecordingId] = useState<number | null>(null);
  const [baselineSaving, setBaselineSaving] = useState(false);

  // Fetch the baseline analysis (if the vehicle has one set) for the
  // diagnostic modal's drift comparison. Re-run on recording change.
  useEffect(() => {
    let cancelled = false;
    api.get<{ baseline_recording_id: number | null; analysis: AnalysisResult | null }>(
      `/api/admin/recordings/${recordingId}/baseline-analysis`,
    ).then((r) => {
      if (cancelled) return;
      setBaselineAnalysis(r.analysis);
      setBaselineRecordingId(r.baseline_recording_id);
    }).catch(() => {
      if (!cancelled) {
        setBaselineAnalysis(null);
        setBaselineRecordingId(null);
      }
    });
    return () => { cancelled = true; };
  }, [recordingId]);

  // Toggle the vehicle's baseline to / from this recording.
  const toggleBaseline = useCallback(async () => {
    if (!meta) return;
    const isAlready = baselineRecordingId === recordingId;
    const target = isAlready ? null : recordingId;
    setBaselineSaving(true);
    try {
      const r = await api.put<{ baseline_recording_id: number | null }>(
        `/api/admin/vehicles/${meta.vehicle.id}/baseline`,
        { recording_id: target },
      );
      setBaselineRecordingId(r.baseline_recording_id);
      // Re-fetch baseline analysis since it's changed.
      const ba = await api.get<{ baseline_recording_id: number | null; analysis: AnalysisResult | null }>(
        `/api/admin/recordings/${recordingId}/baseline-analysis`,
      );
      setBaselineAnalysis(ba.analysis);
    } catch (e) {
      setMetaError((e as Error).message);
    } finally {
      setBaselineSaving(false);
    }
  }, [meta, baselineRecordingId, recordingId]);

  return (
    <>
      <div className="panel">
        <button className="secondary" onClick={onBack}>← Back</button>
        <h3 style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          Recording #{recordingId}
        </h3>
      </div>

      {metaError && <div className="panel error">{metaError}</div>}
      {meta && (
        <div className="panel">
          <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>Metadata</h3>
            <div className="row" style={{ gap: '0.4rem' }}>
              <button
                className="secondary"
                onClick={() => void toggleBaseline()}
                disabled={baselineSaving}
                style={baselineRecordingId === recordingId ? {
                  borderColor: 'var(--ok)', color: 'var(--ok)',
                } : undefined}
                title={baselineRecordingId === recordingId
                  ? 'This recording IS the vehicle\'s baseline. Click to clear.'
                  : 'Mark this recording as the vehicle\'s baseline for drift comparisons.'}
              >
                {baselineSaving ? '…'
                  : baselineRecordingId === recordingId ? '★ Baseline'
                  : 'Set as baseline'}
              </button>
              <button className="secondary" onClick={() => setShowLabelEdit(true)}>
                Edit labels
              </button>
            </div>
          </div>
          <table>
            <tbody>
              <tr><td>Recorded at</td><td>{fmtTs(meta.recorded_at)}</td></tr>
              <tr><td>Vehicle</td><td>{meta.vehicle.name} (id {meta.vehicle.id})</td></tr>
              <tr><td>Trip start km</td><td>{meta.trip.start_km}</td></tr>
              <tr><td>Trip purpose</td><td>{meta.trip.purpose}</td></tr>
              <tr><td>Mic location</td><td>{meta.mic_location}</td></tr>
              <tr><td>Engine</td><td>
                {meta.engine ? (
                  <>
                    {meta.engine.num_cylinders} cyl, {meta.engine.num_strokes}-stroke
                    {meta.engine.num_valves_per_cylinder != null && (
                      <>
                        , {meta.engine.num_valves_per_cylinder} valves/cyl
                        {' '}({meta.engine.total_valves ??
                          meta.engine.num_cylinders * meta.engine.num_valves_per_cylinder} total)
                      </>
                    )}
                  </>
                ) : <span className="muted">(not recorded)</span>}
              </td></tr>
              <tr><td>Audio</td><td>{meta.sample_rate} Hz, {meta.channels} ch, {meta.duration_sec.toFixed(2)} s, {fmtBytes(meta.size_bytes)}</td></tr>
              <tr><td>Labels</td><td>
                {meta.labels && (meta.labels.gauge_rpm != null
                  || meta.labels.powertrain != null
                  || (meta.labels.incident_keywords?.length ?? 0) > 0)
                  ? <LabelTags labels={meta.labels} />
                  : <span className="muted">(none — click Edit labels)</span>}
              </td></tr>
            </tbody>
          </table>
        </div>
      )}

      {showLabelEdit && meta && (
        <AdminLabelModal
          recordingId={recordingId}
          initial={meta.labels ?? { gauge_rpm: null, powertrain: null, incident_keywords: null }}
          onClose={() => setShowLabelEdit(false)}
          onSaved={(saved) => {
            setMeta((prev) => prev ? { ...prev, labels: saved } : prev);
            setShowLabelEdit(false);
          }}
        />
      )}

      {/* Per-cylinder waveform ensembles — operates on the persisted
          analysis (not the in-page Run-analysis result), so it works
          immediately on opening a recording. Lazy-loaded; the user
          clicks "Load ensembles" inside. */}
      <CylinderEnsembleView recordingId={recordingId} />

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Run analysis</h3>
        <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ width: 180 }}>
            <label>Components method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as UnmixMethod)}
              disabled={running}
            >
              <option value="block">Block reshape</option>
              <option value="ssa">SSA</option>
              <option value="ica">ICA (cycle-aligned)</option>
              <option value="nmf">NMF (cycle-aligned)</option>
            </select>
          </div>
          <div className="field" style={{ width: 110 }}>
            <label># cylinders</label>
            <input
              type="number" min={1} max={24} step={1}
              value={numCylinders}
              onChange={(e) => {
                const n = Math.min(24, Math.max(1, Number(e.target.value) || 1));
                setNumCylinders(n);
              }}
              disabled={running}
            />
          </div>
          <div className="field" style={{ width: 130 }}>
            <label>Engine type</label>
            <select
              value={numStrokes}
              onChange={(e) => setNumStrokes(Number(e.target.value) === 2 ? 2 : 4)}
              disabled={running}
            >
              <option value={4}>4-stroke</option>
              <option value={2}>2-stroke</option>
            </select>
          </div>
          <div className="field">
            <button onClick={runAnalysis} disabled={running}>
              {running ? 'Running…' : (save ? 'Run & save analysis' : 'Run (preview only)')}
            </button>
          </div>
        </div>
        <div className="row" style={{ alignItems: 'center', gap: '0.6rem', marginTop: -4 }}>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            margin: 0, fontSize: '0.85rem', color: 'var(--fg)',
          }}>
            <input
              type="checkbox"
              checked={save}
              onChange={(e) => setSave(e.target.checked)}
              disabled={running}
              style={{ width: 'auto' }}
            />
            Save to recording
          </label>
          <span className="muted" style={{ fontSize: '0.78rem' }}>
            {save
              ? 'Persists this analysis as the recording\'s current view (overwrites any prior).'
              : 'Preview only — the recording\'s stored analysis is unchanged.'}
          </span>
          {savedAt && (
            <span style={{ color: 'var(--ok)', fontSize: '0.78rem' }}>
              ✓ saved at {new Date(savedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="muted" style={{ fontSize: '0.85rem' }}>
          # cylinders and engine type are only used by the ICA method, but
          are sent regardless. RMS and spectrogram always run alongside.
        </div>
        {runError && <div className="error" style={{ marginTop: 8 }}>{runError}</div>}
      </div>

      {/* IcaComponentStack and supporting card components are defined at
          the bottom of this file. */}
      {result && (
        <div className="panel">
          <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0 }}>
              Result <span className="muted" style={{ fontSize: '0.85rem' }}>
                ({result.components.method},
                {' '}{result.components.num_components} components
                {result.components.rpm != null
                  && ` · est. RPM ${Math.round(result.components.rpm)}`}
                {result.components.rpm == null
                  && (result.components.rpm_error || result.components.error)
                  && ` · RPM n/a`}
                {isIca && result.components.error
                  && ` · ICA failed`})
              </span>
            </h3>
            <div className="row" style={{ gap: '0.25rem' }}>
              {result.components.cylinders
                && result.components.cylinders.cylinders.length > 0 && (
                <button
                  onClick={() => setShowDiagnostic(true)}
                  title="Open the per-cylinder diagnostic dashboard"
                >Diagnose</button>
              )}
              <button
                className="secondary"
                onClick={() => setView('rms')}
                style={view === 'rms' ? { background: 'var(--accent-2)', color: 'white' } : undefined}
              >RMS</button>
              <button
                className="secondary"
                onClick={() => setView('spectrogram')}
                style={view === 'spectrogram' ? { background: 'var(--accent-2)', color: 'white' } : undefined}
              >Spectrogram</button>
              <button
                className="secondary"
                onClick={() => setView('components')}
                style={view === 'components' ? { background: 'var(--accent-2)', color: 'white' } : undefined}
              >Components</button>
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            {(() => {
              // Recording duration in seconds. Same value for all three
              // result charts since they came from the same FLAC.
              const dur = result.rms.num_blocks * result.rms.block_size / result.rms.sample_rate;
              return (
                <>
                  {meta?.labels && (
                    <LabelStrip labels={meta.labels} />
                  )}
                  <RpmConsensusBox
                    components={result.components}
                    gaugeRpm={meta?.labels?.gauge_rpm ?? null}
                  />
                  {view === 'rms' && (
                    <LiveRmsChart rms={result.rms.rms} height={140} segmentSec={dur} />
                  )}
                  {view === 'spectrogram' && (
                    <LiveSpectrogramChart
                      segments={[{
                        data: result.spectrogram.data,
                        numBlocks: result.spectrogram.num_blocks,
                        numBins: result.spectrogram.num_bins,
                      }]}
                      height={220}
                      segmentSec={dur}
                      sampleRate={result.spectrogram.sample_rate}
                    />
                  )}
                  {view === 'components' && (
                    isIca ? (
                      <IcaComponentStack
                        components={result.components}
                        gaugeRpm={meta?.labels?.gauge_rpm ?? null}
                      />
                    ) : isNmf ? (
                      <NmfComponentStack components={result.components} />
                    ) : (
                      <LiveComponentsChart
                        segments={[{ components: result.components.components }]}
                        height={compHeight}
                        segmentSec={dur}
                      />
                    )
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {result?.diagnostics && (
        <OctaveConsolePanel diagnostics={result.diagnostics} />
      )}

      {showDiagnostic && result && (
        <DiagnosticModal
          result={result}
          recordingId={recordingId}
          vehicleName={meta?.vehicle.name}
          baseline={baselineAnalysis}
          onClose={() => setShowDiagnostic(false)}
        />
      )}
    </>
  );
}

// ---- Octave console panel ----------------------------------------------
//
// Shows, for each of the three Octave subprocesses spawned by the server,
// the exact spawn command (so admin can copy-paste to reproduce locally),
// its exit code + duration, and its stdout / stderr.

function OctaveConsolePanel({ diagnostics }: { diagnostics: import('./api').AnalysisDiagnostics }) {
  const [open, setOpen] = useState<{ rms: boolean; spec: boolean; comp: boolean }>(
    { rms: false, spec: false, comp: true },   // ICA expanded by default
  );
  const sections: Array<{ key: keyof typeof open; label: string; diag: import('./api').OctaveDiagnostic }> = [
    { key: 'rms',  label: 'rms_analysis.m',         diag: diagnostics.rms },
    { key: 'spec', label: 'spectrogram_analysis.m', diag: diagnostics.spectrogram },
    { key: 'comp', label: 'unmix_analysis.m',       diag: diagnostics.components },
  ];
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>
        Octave console
        <span className="muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>
          {' '}— spawn commands, stdout, stderr from each subprocess
        </span>
      </h3>
      {sections.map(({ key, label, diag }) => (
        <div key={key} style={{ marginBottom: '0.6rem' }}>
          <div className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
            <button
              className="secondary"
              onClick={() => setOpen((p) => ({ ...p, [key]: !p[key] }))}
              style={{ minWidth: 28, padding: '0.25rem 0.5rem' }}
              aria-expanded={open[key]}
            >{open[key] ? '▾' : '▸'}</button>
            <strong>{label}</strong>
            <span className="muted" style={{ fontSize: '0.8rem' }}>
              exit {diag.exit_code} · {diag.duration_ms} ms
              · stdout {diag.stdout.length} B · stderr {diag.stderr.length} B
            </span>
          </div>
          {open[key] && (
            <div style={{
              marginTop: 4, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              fontSize: '0.78rem',
            }}>
              <div className="muted" style={{ marginBottom: 2 }}>spawn command (copy-paste):</div>
              <pre style={{
                background: 'var(--panel-2)', border: '1px solid var(--border)',
                borderRadius: 4, padding: '0.5rem', margin: 0,
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                color: 'var(--accent)',
              }}>{quoteCmd(diag.command)}</pre>
              {diag.stdout.length > 0 && (
                <>
                  <div className="muted" style={{ marginTop: 6, marginBottom: 2 }}>stdout:</div>
                  <pre style={consolePre}>{diag.stdout}</pre>
                </>
              )}
              {diag.stderr.length > 0 && (
                <>
                  <div className="muted" style={{ marginTop: 6, marginBottom: 2 }}>stderr:</div>
                  <pre style={{ ...consolePre, color: '#fbbf24' }}>{diag.stderr}</pre>
                </>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const consolePre = {
  background: 'var(--bg)', border: '1px solid var(--border)',
  borderRadius: 4, padding: '0.5rem', margin: 0,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  maxHeight: 240, overflow: 'auto',
} as const;

/** Quote a spawn argv into a shell-paste-safe string. Single-quotes any
 *  argument that contains whitespace or shell-special characters. */
function quoteCmd(argv: string[]): string {
  return argv.map((a) =>
    /^[\w.\/=-]+$/.test(a) ? a : `'${a.replace(/'/g, "'\\''")}'`,
  ).join(' ');
}

// ---- ICA-specific per-component view -----------------------------------
//
// One card per ICA component. Each card shows:
//   • a Morlet wavelet time-frequency tiling (heatmap, viridis log-power),
//   • the time-domain waveform of the component beneath it,
//   • the component's RMS,
//   • a Play / Stop button that loops the per-cycle waveform via Web Audio.
//
// The full per-cycle waveforms come from `components.full_components`; if
// they are missing (e.g. because the recording was analysed before
// includeFullWaveforms was wired), the card falls back to the decimated
// `components` array and disables the wavelet + audio buttons.

interface IcaComponentStackProps {
  components: ComponentsResult;
  /** When set, the RPM banner shows the gauge RPM alongside the
   *  ICA-estimated RPM so admin can compare. */
  gaugeRpm?: number | null;
}

function IcaComponentStack({ components, gaugeRpm }: IcaComponentStackProps) {
  const K = components.num_components;
  if (components.error) {
    return (
      <div className="error" style={{ fontSize: '0.9rem' }}>
        ICA could not estimate the firing-rate period: {components.error}
      </div>
    );
  }
  if (K === 0) {
    return <div className="muted">No components returned.</div>;
  }
  const fullComps = components.full_components ?? [];
  const fullSr = components.full_sample_rate ?? components.sample_rate;
  const numCyl = components.num_cylinders;
  const cycleSec = components.cycle_samples != null
    ? components.cycle_samples / fullSr : undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <RpmDerivationBanner components={components} gaugeRpm={gaugeRpm ?? null} />
      {components.ensemble_trials && components.ensemble_trials.length > 0 && (
        <EnsemblePlot
          trials={components.ensemble_trials}
          numCylinders={numCyl}
          durationSec={cycleSec}
        />
      )}
      {Array.from({ length: K }, (_, k) => {
        const full = fullComps[k];
        const decimated = components.components[k] ?? [];
        const rms = components.component_rms?.[k];
        return (
          <IcaComponentCard
            key={k}
            index={k}
            full={full ? Float32Array.from(full) : null}
            decimated={decimated}
            sampleRate={fullSr}
            rms={rms}
            numCylinders={numCyl}
            strokeSamples={components.stroke_samples}
            cycleSamples={components.cycle_samples}
          />
        );
      })}
    </div>
  );
}

// Big RPM badge above the per-component stack. RPM does NOT come from any
// single component — it's the autocorrelation peak on the input signal.
// The note makes that explicit so admin doesn't misread the per-component
// stroke marks as the RPM-determining feature.
function RpmDerivationBanner({
  components, gaugeRpm,
}: { components: ComponentsResult; gaugeRpm: number | null }) {
  const rpm = components.rpm;
  const fDom = components.dominant_freq_hz;
  const M = components.stroke_samples;
  const fs = components.full_sample_rate ?? components.sample_rate;
  const cyl = components.num_cylinders;
  const strokes = components.num_strokes;
  if (rpm == null) return null;
  return (
    <div
      style={{
        background: 'var(--panel-2)',
        border: '1px solid var(--accent-2)',
        borderRadius: 8,
        padding: '0.7rem 1rem',
        display: 'flex',
        alignItems: 'baseline',
        gap: '1.2rem',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div className="muted" style={{ fontSize: '0.8rem' }}>ICA-estimated RPM</div>
        <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--accent)' }}>
          {Math.round(rpm)}
        </div>
      </div>
      {gaugeRpm != null && (
        <div>
          <div className="muted" style={{ fontSize: '0.8rem' }}>Gauge RPM (label)</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f59e0b' }}>
            {gaugeRpm}
          </div>
          <div className="muted" style={{ fontSize: '0.75rem', marginTop: -4 }}>
            Δ = {Math.round(rpm) - gaugeRpm} RPM
          </div>
        </div>
      )}
      <div className="muted" style={{ fontSize: '0.8rem', maxWidth: 600 }}>
        Derived from <strong>autocorrelation on the input signal</strong>{' '}
        (not any single ICA component). Peak ACF at lag M = <strong>{M}</strong> samples
        @ fs = {fs} Hz ⇒ firing rate = <strong>{fDom?.toFixed(2)} Hz</strong>.
        For a {strokes}-stroke {cyl}-cylinder engine,
        RPM = firing rate × 60 / (cyl/{strokes === 4 ? 2 : 1}) =
        <strong> {Math.round(rpm)}</strong>.
        The dashed stroke markers on each card below visualize this period —
        they are spaced M samples apart, dividing each engine cycle into {cyl}{' '}
        equal stroke segments.
      </div>
    </div>
  );
}

interface IcaComponentCardProps {
  index: number;
  /** Full per-cycle waveform at the original sample rate, or null if the
   *  result didn't carry full_components (then audio + wavelet are off). */
  full: Float32Array | null;
  /** Decimated waveform for the time-domain plot (always present). */
  decimated: number[];
  sampleRate: number;
  rms?: number;
  /** Engine geometry — used to draw stroke-boundary markers. */
  numCylinders?: number;
  /** Stroke spacing in samples (M). */
  strokeSamples?: number;
  /** numCylinders × strokeSamples — the full-waveform span. */
  cycleSamples?: number;
}

function IcaComponentCard({
  index, full, decimated, sampleRate, rms,
  numCylinders, strokeSamples, cycleSamples,
}: IcaComponentCardProps) {
  const [playing, setPlaying] = useState(false);
  const playRef = useRef<PlayHandle | null>(null);

  const togglePlay = useCallback(() => {
    if (playing) {
      playRef.current?.stop();
      playRef.current = null;
      setPlaying(false);
      return;
    }
    if (!full) return;
    const handle = playLoopedClip(full, sampleRate, 3);
    playRef.current = handle;
    setPlaying(true);
    void handle.done.then(() => {
      if (playRef.current === handle) {
        playRef.current = null;
        setPlaying(false);
      }
    });
  }, [playing, full, sampleRate]);

  // Stop on unmount.
  useEffect(() => () => { playRef.current?.stop(); }, []);

  return (
    <div
      style={{
        background: 'var(--panel-2)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '0.6rem 0.8rem',
      }}
    >
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div>
          <strong>comp{index + 1}</strong>
          {rms != null && (
            <span className="muted" style={{ marginLeft: '0.6rem', fontSize: '0.85rem' }}>
              RMS: <strong style={{ color: 'var(--fg)' }}>{rms.toExponential(3)}</strong>
            </span>
          )}
        </div>
        <button
          className="secondary"
          onClick={togglePlay}
          disabled={!full}
          title={full ? 'Loop the per-cycle waveform via Web Audio' : 'No full waveform available — re-run analysis'}
        >
          {playing ? '■ Stop' : '▶ Play'}
        </button>
      </div>
      <WaveletHeatmap
        signal={full}
        sampleRate={sampleRate}
        numCylinders={numCylinders}
      />
      <ComponentWaveform
        values={decimated}
        numCylinders={numCylinders}
        durationSec={cycleSamples != null ? cycleSamples / sampleRate : undefined}
      />
      {numCylinders != null && strokeSamples != null && cycleSamples != null && (
        <div className="muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>
          Dashed lines: expected stroke boundaries — one cycle ={' '}
          {numCylinders} strokes × M = {strokeSamples} samples. Spacing M
          comes from the autocorrelation peak on the input signal (see
          banner above), <em>not</em> from this component.
        </div>
      )}
    </div>
  );
}

// ---- Wavelet heatmap (canvas) ------------------------------------------

interface WaveletHeatmapProps {
  signal: Float32Array | null;
  sampleRate: number;
  numCylinders?: number;
}

const WAVELET_HEATMAP_PX_HEIGHT = 120;

function WaveletHeatmap({ signal, sampleRate, numCylinders }: WaveletHeatmapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [computeError, setComputeError] = useState<string | null>(null);
  // Track the band actually used for the most recent render so the
  // FreqAxis matches.
  const [band, setBand] = useState<{ fMin: number; fMax: number } | null>(null);

  // Choose frequency band: from a hair above DC to half Nyquist. For
  // engine audio that's typically [50, fs/4] Hz. Computed eagerly so the
  // axis renders even before the canvas effect fires.
  const fMax = Math.min(8000, sampleRate / 4);
  const fMin = Math.max(50, fMax / 200);
  const durationSec = signal ? signal.length / sampleRate : 0;

  useEffect(() => {
    if (!signal || signal.length < 64) return;
    setComputeError(null);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const numScales = 60;
    let cwt;
    try {
      cwt = morletCWT(signal, logScales(fMin, fMax, numScales, sampleRate), sampleRate);
    } catch (e) {
      setComputeError((e as Error).message);
      return;
    }
    setBand({ fMin, fMax });

    // Render. Canvas pixel size = (numTimes × numScales); CSS upscales.
    const W = cwt.numTimes;
    const H = cwt.numScales;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Log scaling so a bright manifold resonance doesn't drown out the
    // surrounding structure.
    let pMax = 0;
    for (let i = 0; i < cwt.power.length; i++) {
      if (cwt.power[i] > pMax) pMax = cwt.power[i];
    }
    const eps = pMax * 1e-6 + 1e-30;
    const dbMin = -60;            // 60 dB dynamic range below the peak
    const img = ctx.createImageData(W, H);
    for (let s = 0; s < H; s++) {
      // s = 0 is the highest frequency (top row) — already so by
      // construction in logScales().
      for (let t = 0; t < W; t++) {
        const v = cwt.power[s * W + t];
        const db = 10 * Math.log10((v + eps) / pMax);
        const clamped = Math.max(dbMin, Math.min(0, db));
        const norm = (clamped - dbMin) / -dbMin;
        const [R, G, B] = viridis(norm);
        const idx = (s * W + t) * 4;
        img.data[idx] = R;
        img.data[idx + 1] = G;
        img.data[idx + 2] = B;
        img.data[idx + 3] = 0xff;
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [signal, sampleRate, fMin, fMax]);

  if (!signal) {
    return (
      <div className="muted" style={{ fontSize: '0.85rem', padding: '0.5rem 0' }}>
        Time-frequency view unavailable (no full waveform in this result).
      </div>
    );
  }

  const axisFMin = band?.fMin ?? fMin;
  const axisFMax = band?.fMax ?? fMax;
  return (
    <div>
      <div className="muted" style={{ fontSize: '0.75rem', marginBottom: 2 }}>
        {WAVELET_NAME} · log power, 60 dB dynamic range
      </div>
      <div style={{ display: 'flex' }}>
        <FreqAxis
          minHz={axisFMin}
          maxHz={axisFMax}
          height={WAVELET_HEATMAP_PX_HEIGHT}
          log
        />
        <div style={{ flex: 1, minWidth: 0, position: 'relative', height: WAVELET_HEATMAP_PX_HEIGHT }}>
          <canvas
            ref={canvasRef}
            style={{
              width: '100%',
              height: '100%',
              imageRendering: 'pixelated',
              background: '#000',
              display: 'block',
            }}
          />
          {numCylinders != null && numCylinders > 0 && (
            <StrokeOverlay
              numCylinders={numCylinders}
              color="rgba(255,255,255,0.55)"
              withLabels
            />
          )}
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ width: 44, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <TimeAxis segmentSec={durationSec} />
        </div>
      </div>
      {computeError && (
        <div className="error" style={{ fontSize: '0.8rem' }}>{computeError}</div>
      )}
    </div>
  );
}

// ---- Tiny time-domain waveform plot ------------------------------------

function ComponentWaveform({
  values, numCylinders, durationSec,
}: { values: number[]; numCylinders?: number; durationSec?: number }) {
  if (values.length < 2) {
    return <div className="muted" style={{ fontSize: '0.85rem' }}>no waveform</div>;
  }
  const W = 1200;
  const H = 60;
  let mn = Infinity, mx = -Infinity;
  for (const v of values) { if (v < mn) mn = v; if (v > mx) mx = v; }
  if (mx - mn < 1e-12) { mn -= 1; mx += 1; }
  const denom = values.length - 1;
  const points = values.map((v, i) => {
    const x = (i / denom) * W;
    const y = H - ((v - mn) / (mx - mn)) * H;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');
  const svg = (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: H, display: 'block', marginTop: 4 }}
      aria-label="component waveform"
    >
      {numCylinders != null && numCylinders > 0 &&
        Array.from({ length: numCylinders + 1 }, (_, k) => {
          const x = (k / numCylinders) * W;
          return (
            <line
              key={`stroke-${k}`}
              x1={x} y1={0} x2={x} y2={H}
              stroke="rgba(245,158,11,0.65)"
              strokeWidth="1"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      <polyline
        points={points}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
  if (durationSec == null || durationSec <= 0) return svg;
  return (
    <div>
      {svg}
      <TimeAxis segmentSec={durationSec} />
    </div>
  );
}

// ---- Trial ensemble plot (alignment debug) -----------------------------
//
// Renders the matrix that was actually fed to runica: 10 stacked rows,
// one engine cycle each, anchored on the envelope-peak inside the first
// scan-window cycle. Vertical dashed lines mark the expected stroke
// boundaries — if alignment is correct, every row's firing peaks should
// land at the same x position.
//
// This is the canonical debug plot to verify that `unmix_ica.m` is
// chopping the recording at the right phase. If the rows look smeared
// or peaks drift across rows, alignment is failing — likely because the
// envelope-peak anchor latched onto the wrong lobe, or the autocorr-
// derived M doesn't match the actual stroke spacing.

interface EnsemblePlotProps {
  trials: number[][];
  numCylinders?: number;
  /** Engine-cycle duration in seconds (for the time axis). */
  durationSec?: number;
}

function EnsemblePlot({ trials, numCylinders, durationSec }: EnsemblePlotProps) {
  const N = trials.length;
  const M = trials[0]?.length ?? 0;
  if (N === 0 || M === 0) return null;
  const W = 1200;
  const laneH = 28;
  const totalH = N * laneH;
  const ranges = trials.map((row) => {
    let mn = Infinity, mx = -Infinity;
    for (const v of row) { if (v < mn) mn = v; if (v > mx) mx = v; }
    if (mx - mn < 1e-12) { mn -= 1; mx += 1; }
    return { mn, range: mx - mn };
  });
  return (
    <div style={{
      background: 'var(--panel-2)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: '0.6rem 0.8rem',
    }}>
      <div style={{ marginBottom: 4 }}>
        <strong>Trial ensemble fed to runica</strong>
        <span className="muted" style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}>
          ({N} rows × one engine cycle each — alignment debug)
        </span>
      </div>
      <div className="muted" style={{ fontSize: '0.75rem', marginBottom: 4 }}>
        If alignment is correct, the firing peaks in each row should line up
        vertically with the dashed stroke boundaries. Rows independently
        normalised so even quiet cycles are visible.
      </div>
      <svg
        viewBox={`0 0 ${W} ${totalH}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: totalH * 1.6, display: 'block' }}
        aria-label="ICA input ensemble (trial-aligned cycles)"
      >
        {/* Stroke-boundary lines (under the traces) */}
        {numCylinders != null && numCylinders > 0 &&
          Array.from({ length: numCylinders + 1 }, (_, k) => {
            const x = (k / numCylinders) * W;
            return (
              <line
                key={`stroke-${k}`}
                x1={x} y1={0} x2={x} y2={totalH}
                stroke="rgba(245,158,11,0.55)"
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        {/* Lane separators */}
        {Array.from({ length: N - 1 }, (_, i) => {
          const y = (i + 1) * laneH;
          return (
            <line
              key={`sep-${i}`}
              x1={0} y1={y} x2={W} y2={y}
              stroke="var(--border)" strokeWidth="0.5"
            />
          );
        })}
        {/* Trial labels */}
        {trials.map((_, k) => (
          <text
            key={`lbl-${k}`}
            x={6} y={k * laneH + 14}
            fill="var(--muted)"
            fontSize="11"
            fontFamily="ui-monospace, SFMono-Regular, monospace"
          >trial {k + 1}</text>
        ))}
        {/* Stroke-boundary numeric labels at top of plot (1..N) */}
        {numCylinders != null && numCylinders > 0 &&
          Array.from({ length: numCylinders }, (_, k) => {
            const x = ((k + 0.5) / numCylinders) * W;
            return (
              <text
                key={`stroke-lbl-${k}`}
                x={x} y={11}
                textAnchor="middle"
                fill="rgba(245,158,11,0.85)"
                fontSize="10"
                fontFamily="ui-monospace, SFMono-Regular, monospace"
              >{k + 1}</text>
            );
          })}
        {/* Per-trial trace */}
        {trials.map((row, k) => {
          const { mn, range } = ranges[k];
          const top = k * laneH + 4;
          const traceH = laneH - 8;
          const denom = row.length - 1;
          const points = row.map((v, i) => {
            const x = (i / denom) * W;
            const y = top + (1 - (v - mn) / range) * traceH;
            return `${x.toFixed(2)},${y.toFixed(2)}`;
          }).join(' ');
          return (
            <polyline
              key={`row-${k}`}
              points={points}
              fill="none"
              stroke="var(--accent)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </svg>
      {durationSec != null && durationSec > 0 && (
        <TimeAxis segmentSec={durationSec} />
      )}
    </div>
  );
}

/** SVG overlay drawing N+1 vertical stroke-boundary lines and (optionally)
 *  numbered labels between them. Uses preserveAspectRatio=none + 0..1 view
 *  box so it stretches to the parent's full width regardless of pixel
 *  size. Positioned absolutely on top of a canvas/heatmap. */
function StrokeOverlay({
  numCylinders, color, withLabels,
}: { numCylinders: number; color: string; withLabels?: boolean }) {
  return (
    <svg
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        pointerEvents: 'none',
      }}
    >
      {Array.from({ length: numCylinders + 1 }, (_, k) => {
        const x = k / numCylinders;
        return (
          <line
            key={`b-${k}`}
            x1={x} y1={0} x2={x} y2={1}
            stroke={color} strokeWidth="0.003" strokeDasharray="0.012 0.012"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
      {withLabels && Array.from({ length: numCylinders }, (_, k) => {
        const xCenter = (k + 0.5) / numCylinders;
        return (
          <text
            key={`lbl-${k}`}
            x={xCenter} y={0.08}
            textAnchor="middle"
            fontSize="0.075"
            fill="rgba(255,255,255,0.85)"
            style={{ fontFamily: 'ui-monospace, monospace', paintOrder: 'stroke' }}
            stroke="rgba(0,0,0,0.6)"
            strokeWidth="0.005"
          >{k + 1}</text>
        );
      })}
    </svg>
  );
}

/** 5-stop viridis colormap (mirrors RmsChart.tsx; kept local so this file
 *  stays self-contained). */
function viridis(t: number): [number, number, number] {
  const stops: Array<[number, [number, number, number]]> = [
    [0,    [68,  1,   84 ]],
    [0.25, [59,  82,  139]],
    [0.5,  [33,  144, 140]],
    [0.75, [93,  201, 99 ]],
    [1,    [253, 231, 37 ]],
  ];
  if (t <= 0) return stops[0][1];
  if (t >= 1) return stops[stops.length - 1][1];
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i][0]) {
      const [t0, c0] = stops[i - 1];
      const [t1, c1] = stops[i];
      const f = (t - t0) / (t1 - t0);
      return [
        Math.round(c0[0] + (c1[0] - c0[0]) * f),
        Math.round(c0[1] + (c1[1] - c0[1]) * f),
        Math.round(c0[2] + (c1[2] - c0[2]) * f),
      ];
    }
  }
  return stops[stops.length - 1][1];
}

// ---- Shared RPM consensus box (any method) -----------------------------
//
// Surfaces the four-detector consensus + per-detector breakdown above
// every chart. When the four agree, the consensus is high-confidence;
// when they split, the disagreement IS the diagnostic (typically misfire
// or bank-asymmetry). For ICA, the detailed RpmDerivationBanner inside
// IcaComponentStack adds the M-from-autocorr explanation; this lighter
// box covers the other methods (and ICA's component view, where both
// are visible).

function RpmConsensusBox({
  components, gaugeRpm,
}: { components: ComponentsResult; gaugeRpm: number | null }) {
  const [expanded, setExpanded] = useState(false);
  const c = components;
  const methods = c.rpm_methods ?? [];
  const total = methods.length;
  const numAgree = c.rpm != null
    ? methods.filter((m) => m.corrected_rpm != null && Math.abs(m.corrected_rpm - c.rpm!) <= 0.05 * c.rpm!).length
    : 0;
  const tone = c.rpm_agreement === 'all' ? 'var(--ok)'
             : c.rpm_agreement === 'majority' ? '#f59e0b'
             : 'var(--danger)';
  const wasCorrected = c.rpm_correction && c.rpm_correction !== 'none'
    && c.rpm != null && c.rpm_raw != null && Math.abs(c.rpm - c.rpm_raw) > 0.01 * c.rpm;

  if (c.rpm == null && total === 0 && !c.rpm_error && !c.error) return null;

  return (
    <div style={{
      background: 'var(--panel-2)',
      border: '1px solid var(--border)',
      borderRadius: 6,
      padding: '0.5rem 0.7rem',
      marginBottom: '0.5rem',
      fontSize: '0.85rem',
    }}>
      <div className="row" style={{ alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <span className="muted">RPM (consensus, harmonic-corrected):</span>
        {c.rpm != null ? (
          <strong style={{ color: 'var(--accent)', fontSize: '1rem' }}>
            {Math.round(c.rpm)}
          </strong>
        ) : (
          <span className="error">— ({c.rpm_error || c.error || 'no consensus'})</span>
        )}
        {wasCorrected && (
          <span className="muted" style={{ fontSize: '0.78rem' }}>
            (raw {Math.round(c.rpm_raw!)}, via {c.rpm_correction})
          </span>
        )}
        {gaugeRpm != null && (
          <>
            <span className="muted">·</span>
            <span className="muted">gauge:</span>
            <strong style={{ color: '#f59e0b' }}>{gaugeRpm}</strong>
            {c.rpm != null && (
              <span className="muted" style={{ fontSize: '0.78rem' }}>
                Δ {Math.round(c.rpm) - gaugeRpm} RPM
              </span>
            )}
          </>
        )}
        {total > 0 && (
          <span style={{ color: tone, fontSize: '0.8rem' }}>
            ({numAgree}/{total} agree, {c.rpm_agreement})
          </span>
        )}
        {total > 0 && (
          <button
            className="secondary"
            style={{ marginLeft: 'auto', padding: '0.2rem 0.55rem', fontSize: '0.78rem' }}
            onClick={() => setExpanded((v) => !v)}
          >{expanded ? 'hide detectors' : 'show detectors'}</button>
        )}
      </div>
      {c.rpm_diagnostic
        && c.rpm_diagnostic !== 'no harmonic locking — clean signal, no asymmetry detected' && (
        <div style={{
          marginTop: 6, fontSize: '0.82rem',
          padding: '0.3rem 0.5rem',
          background: wasCorrected ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
          borderLeft: wasCorrected ? '3px solid #f59e0b' : 'none',
          borderRadius: 4,
        }}>
          <strong>Diagnostic:</strong> {c.rpm_diagnostic}
        </div>
      )}
      {expanded && total > 0 && (
        <table style={{ marginTop: 6, fontSize: '0.78rem',
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
          <thead>
            <tr style={{ color: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ textAlign: 'left' }}>method</th>
              <th style={{ textAlign: 'right' }}>raw RPM</th>
              <th style={{ textAlign: 'right' }}>fDom (Hz)</th>
              <th style={{ textAlign: 'right' }}>×</th>
              <th style={{ textAlign: 'right' }}>corrected</th>
              <th style={{ textAlign: 'left', paddingLeft: 8 }}>note</th>
            </tr>
          </thead>
          <tbody>
            {methods.map((m) => {
              const inCluster = c.rpm != null && m.corrected_rpm != null
                && Math.abs(m.corrected_rpm - c.rpm) <= 0.05 * c.rpm;
              const rowTone = m.error ? 'var(--muted)'
                            : inCluster ? 'var(--ok)'
                            : 'var(--danger)';
              const lfColor = m.lock_factor === 1 ? 'var(--ok)'
                            : m.lock_factor === 2 ? '#f59e0b'
                            : m.lock_factor === 3 ? '#fb7185'
                            : m.lock_factor === 6 ? 'var(--danger)'
                            : 'var(--muted)';
              const lfNote = m.lock_factor === 1 ? 'clean'
                           : m.lock_factor === 2 ? 'bank-alt'
                           : m.lock_factor === 3 ? 'per-rev'
                           : m.lock_factor === 6 ? 'engine-cycle'
                           : '';
              return (
                <tr key={m.method} style={{ color: rowTone }}>
                  <td>{m.method}</td>
                  <td style={{ textAlign: 'right' }}>
                    {m.rpm == null ? '—' : Math.round(m.rpm)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {m.fDom_hz == null ? '—' : m.fDom_hz.toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'right', color: lfColor }}>
                    {m.lock_factor == null ? '—' : m.lock_factor}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {m.corrected_rpm == null ? '—' : Math.round(m.corrected_rpm)}
                  </td>
                  <td className="muted" style={{ paddingLeft: 8 }}>
                    {m.error || lfNote || (inCluster ? 'in consensus' : 'outlier')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---- NMF per-component view --------------------------------------------
//
// Per-component card showing:
//   • Spectral pattern W_k (frequency profile of this source mode), as a
//     log-power line plot on a log-frequency x-axis.
//   • Cycle-phase activation H_k (when in the engine cycle this mode
//     fires), as a line plot with stroke-boundary markers.
//   • Total energy of the component as the "RMS" header.
//   • Audio playback button (uses full_components, which carries a
//     synthesised per-cycle waveform from the server).

function NmfComponentStack({ components }: { components: ComponentsResult }) {
  if (components.error) {
    return (
      <div className="error" style={{ fontSize: '0.9rem' }}>
        NMF could not run: {components.error}
      </div>
    );
  }
  const K = components.num_components;
  if (K === 0) return <div className="muted">No components returned.</div>;

  const W = components.nmf_W;
  const H = components.nmf_H;
  const freqs = components.nmf_freqs_hz;
  const fullComps = components.full_components ?? [];
  const fullSr = components.full_sample_rate ?? components.sample_rate;
  const numCyl = components.num_cylinders;
  const cycleSec = components.cycle_samples != null
    ? components.cycle_samples / fullSr : undefined;

  if (!W || !H || !freqs) {
    return (
      <div className="muted" style={{ fontSize: '0.85rem' }}>
        NMF matrices missing — re-run analysis with includeFullWaveforms.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div className="muted" style={{ fontSize: '0.8rem' }}>
        Each card: <strong>spectral pattern W_k</strong> (left, log-freq)
        and <strong>cycle-phase activation H_k</strong> (right, with stroke
        boundaries). Sorted by total energy.
      </div>
      {Array.from({ length: K }, (_, k) => {
        const wCol = W.map((row) => row[k]);
        const hRow = H[k];
        const energy = components.singular_values[k];
        const full = fullComps[k];
        return (
          <NmfComponentCard
            key={k}
            index={k}
            wCol={wCol}
            hRow={hRow}
            freqs={freqs}
            energy={energy}
            full={full ? Float32Array.from(full) : null}
            sampleRate={fullSr}
            numCylinders={numCyl}
            durationSec={cycleSec}
          />
        );
      })}
    </div>
  );
}

interface NmfComponentCardProps {
  index: number;
  wCol: number[];
  hRow: number[];
  freqs: number[];
  energy: number;
  full: Float32Array | null;
  sampleRate: number;
  numCylinders?: number;
  durationSec?: number;
}

function NmfComponentCard({
  index, wCol, hRow, freqs, energy, full, sampleRate,
  numCylinders, durationSec,
}: NmfComponentCardProps) {
  const [playing, setPlaying] = useState(false);
  const playRef = useRef<PlayHandle | null>(null);

  const togglePlay = useCallback(() => {
    if (playing) {
      playRef.current?.stop();
      playRef.current = null;
      setPlaying(false);
      return;
    }
    if (!full) return;
    const handle = playLoopedClip(full, sampleRate, 3);
    playRef.current = handle;
    setPlaying(true);
    void handle.done.then(() => {
      if (playRef.current === handle) {
        playRef.current = null;
        setPlaying(false);
      }
    });
  }, [playing, full, sampleRate]);
  useEffect(() => () => { playRef.current?.stop(); }, []);

  return (
    <div style={{
      background: 'var(--panel-2)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: '0.6rem 0.8rem',
    }}>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div>
          <strong>comp{index + 1}</strong>
          <span className="muted" style={{ marginLeft: '0.6rem', fontSize: '0.85rem' }}>
            energy: <strong style={{ color: 'var(--fg)' }}>{energy.toExponential(3)}</strong>
          </span>
        </div>
        <button
          className="secondary"
          onClick={togglePlay}
          disabled={!full}
          title={full ? 'Loop the per-cycle envelope via Web Audio' : 'No waveform available'}
        >{playing ? '■ Stop' : '▶ Play'}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem' }}>
        <div>
          <div className="muted" style={{ fontSize: '0.75rem', marginBottom: 2 }}>
            spectral pattern W_k (log freq)
          </div>
          <NmfSpectrumPlot wCol={wCol} freqs={freqs} />
        </div>
        <div>
          <div className="muted" style={{ fontSize: '0.75rem', marginBottom: 2 }}>
            cycle-phase activation H_k
          </div>
          <NmfActivationPlot hRow={hRow} numCylinders={numCylinders} />
          {durationSec != null && durationSec > 0 && (
            <TimeAxis segmentSec={durationSec} />
          )}
        </div>
      </div>
    </div>
  );
}

function NmfSpectrumPlot({ wCol, freqs }: { wCol: number[]; freqs: number[] }) {
  const W = 600, H = 80;
  const minFreq = Math.max(20, freqs[1] || 20);
  const maxFreq = freqs[freqs.length - 1] || 1;
  const logMin = Math.log10(minFreq);
  const logMax = Math.log10(maxFreq);
  const logEps = 1e-12;
  let minLog = Infinity, maxLog = -Infinity;
  for (const v of wCol) {
    const lv = Math.log10(Math.max(v, logEps));
    if (lv < minLog) minLog = lv;
    if (lv > maxLog) maxLog = lv;
  }
  if (maxLog - minLog < 1e-6) { minLog -= 1; maxLog += 1; }
  const points = wCol.map((v, i) => {
    const f = freqs[i] ?? 0;
    if (f < minFreq) return null;
    const x = ((Math.log10(f) - logMin) / (logMax - logMin)) * W;
    const lv = Math.log10(Math.max(v, logEps));
    const y = H - ((lv - minLog) / (maxLog - minLog)) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).filter(Boolean).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: '100%', height: H, display: 'block' }}>
      <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="1"
                vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function NmfActivationPlot({
  hRow, numCylinders,
}: { hRow: number[]; numCylinders?: number }) {
  const W = 600, H = 80;
  let mn = Infinity, mx = -Infinity;
  for (const v of hRow) { if (v < mn) mn = v; if (v > mx) mx = v; }
  if (mx - mn < 1e-12) { mn -= 1; mx += 1; }
  const denom = hRow.length - 1;
  const points = hRow.map((v, i) => {
    const x = (i / denom) * W;
    const y = H - ((v - mn) / (mx - mn)) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: '100%', height: H, display: 'block' }}>
      {numCylinders != null && numCylinders > 0 &&
        Array.from({ length: numCylinders + 1 }, (_, k) => {
          const x = (k / numCylinders) * W;
          return (
            <line key={k} x1={x} y1={0} x2={x} y2={H}
                  stroke="rgba(245,158,11,0.55)" strokeWidth="1" strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke" />
          );
        })}
      <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="1"
                vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ---- Label display + edit ----------------------------------------------

function LabelTags({ labels }: { labels: RecordingLabels }) {
  const tags = labels.incident_keywords
    ? labels.incident_keywords.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  if (labels.gauge_rpm == null && labels.powertrain == null && tags.length === 0) {
    return <span className="muted">—</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {labels.gauge_rpm != null && (
        <span className="tag" title="RPM from dashboard gauge">
          gauge {labels.gauge_rpm}
        </span>
      )}
      {labels.powertrain != null && (
        <span className="tag" title="Powertrain type">{labels.powertrain}</span>
      )}
      {tags.map((t) => (
        <span
          key={t}
          className="tag"
          style={{ borderColor: '#f59e0b', color: '#f59e0b' }}
          title="Incident keyword"
        >{t}</span>
      ))}
    </div>
  );
}

/** Strip rendered above each result chart, summarising the labels so they
 *  are visible alongside whatever plot the admin is looking at. */
function LabelStrip({ labels }: { labels: RecordingLabels }) {
  const has = labels.gauge_rpm != null || labels.powertrain != null
    || (labels.incident_keywords?.length ?? 0) > 0;
  if (!has) return null;
  return (
    <div style={{
      background: 'var(--panel-2)',
      border: '1px solid var(--border)',
      borderRadius: 6,
      padding: '0.4rem 0.6rem',
      marginBottom: '0.5rem',
      fontSize: '0.85rem',
      display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap',
    }}>
      <span className="muted" style={{ fontSize: '0.75rem' }}>labels →</span>
      <LabelTags labels={labels} />
    </div>
  );
}

interface AdminLabelModalProps {
  recordingId: number;
  initial: RecordingLabels;
  onClose: () => void;
  onSaved: (labels: RecordingLabels) => void;
}

function AdminLabelModal({
  recordingId, initial, onClose, onSaved,
}: AdminLabelModalProps) {
  const [gauge, setGauge] = useState<string>(
    initial.gauge_rpm == null ? '' : String(initial.gauge_rpm),
  );
  const [pt, setPt] = useState<'' | 'engine' | 'ev'>(initial.powertrain ?? '');
  const [kw, setKw] = useState<string>(initial.incident_keywords ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const saved = await api.put<RecordingLabels & { id: number }>(
        `/api/admin/recordings/${recordingId}/labels`, {
          gauge_rpm: gauge.trim() === '' ? null : Number(gauge),
          powertrain: pt === '' ? null : pt,
          incident_keywords: kw.trim() === '' ? null : kw.trim(),
        },
      );
      onSaved({
        gauge_rpm: saved.gauge_rpm ?? null,
        powertrain: saved.powertrain ?? null,
        incident_keywords: saved.incident_keywords ?? null,
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ background: 'var(--panel)', border: '1px solid var(--border)',
                 borderRadius: 10, padding: '1.25rem', maxWidth: 480, width: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
          <h3 style={{ margin: 0 }}>Edit labels — recording #{recordingId}</h3>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="close"
            style={{ background: 'transparent', border: 0, color: 'var(--muted)',
                     fontSize: '1.5rem', cursor: 'pointer', padding: '0 0.3rem' }}
          >×</button>
        </div>
        <form onSubmit={submit}>
          <div className="field">
            <label>RPM from gauge</label>
            <input
              type="number" min={0} max={20000} step={1}
              value={gauge}
              onChange={(e) => setGauge(e.target.value)}
              placeholder="e.g. 1500"
              style={{ width: '100%' }}
            />
          </div>
          <div className="field">
            <label>Powertrain</label>
            <select
              value={pt}
              onChange={(e) => setPt(e.target.value as '' | 'engine' | 'ev')}
              style={{ width: '100%' }}
            >
              <option value="">— unspecified —</option>
              <option value="engine">Engine (combustion)</option>
              <option value="ev">EV (electric)</option>
            </select>
          </div>
          <div className="field">
            <label>Incident keywords</label>
            <input
              type="text"
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="e.g. knock, misfire, cold start"
              maxLength={500}
              style={{ width: '100%' }}
            />
            <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
              Comma-separated. Up to 500 characters.
            </div>
          </div>
          {err && <div className="field error">{err}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="secondary" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={busy}>{busy ? '…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Phase 3 inspection panel (Steps J / K / L) ---------------------
//
// Per-vehicle Welford metric stats, reservoir contents, and recent
// drift events. Read-only — the data updates automatically as new
// recordings come in (or admin re-analyzes with Save on). Lazy-
// loaded on tab toggle so VehicleView startup stays cheap.

type Phase3Tab = 'metrics' | 'reservoir' | 'drift' | 'classifier';

interface MetricStatRow {
  metric_key: string;
  n: number; mean: number; std: number;
  warn_low: number; warn_high: number;
}
interface ReservoirEntry { id: number; recording_id: number; added_at: number; }
interface DriftEvent {
  id: number; metric_key: string; detected_at: number;
  description: string | null; baseline_mean: number | null;
  current_mean: number | null; magnitude_sigmas: number | null;
}

function Phase3Panel({ vehicleId }: { vehicleId: number }) {
  const [tab, setTab] = useState<Phase3Tab>('metrics');
  const [stats, setStats] = useState<MetricStatRow[] | null>(null);
  const [reservoir, setReservoir] = useState<ReservoirEntry[] | null>(null);
  const [drift, setDrift] = useState<DriftEvent[] | null>(null);
  const [classifier, setClassifier] = useState<ClassifierStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setError(null);
    if (tab === 'metrics' && stats == null) {
      api.get<{ stats: MetricStatRow[] }>(`/api/admin/vehicles/${vehicleId}/metric-stats`)
        .then((r) => setStats(r.stats))
        .catch((e) => setError((e as Error).message));
    }
    if (tab === 'reservoir' && reservoir == null) {
      api.get<{ entries: ReservoirEntry[] }>(`/api/admin/vehicles/${vehicleId}/reservoir`)
        .then((r) => setReservoir(r.entries))
        .catch((e) => setError((e as Error).message));
    }
    if (tab === 'drift' && drift == null) {
      api.get<{ events: DriftEvent[] }>(`/api/admin/vehicles/${vehicleId}/drift-events`)
        .then((r) => setDrift(r.events))
        .catch((e) => setError((e as Error).message));
    }
    if (tab === 'classifier' && classifier == null) {
      api.get<ClassifierStatus>(`/api/admin/vehicles/${vehicleId}/classifier`)
        .then((r) => setClassifier(r))
        .catch((e) => setError((e as Error).message));
    }
  }
  useEffect(load, [tab, vehicleId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="panel">
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>
          Phase 3 + 4 (continual learning + classifier)
          <span className="muted" style={{ fontSize: '0.78rem', fontWeight: 400 }}>
            {' '}— Welford stats, reservoir, drift events, per-vehicle SVM
          </span>
        </h3>
        <div className="row" style={{ gap: 4 }}>
          {(['metrics', 'reservoir', 'drift', 'classifier'] as const).map((t) => (
            <button key={t}
              className={tab === t ? undefined : 'secondary'}
              onClick={() => setTab(t)}
              style={{ fontSize: '0.85rem', padding: '0.3rem 0.7rem' }}
            >
              {t === 'metrics' ? 'Metric stats (J)'
                : t === 'reservoir' ? 'Reservoir (K)'
                : t === 'drift' ? 'Drift events (L)'
                : 'Classifier (N–R)'}
            </button>
          ))}
        </div>
      </div>
      {error && <div className="error" style={{ marginTop: 6 }}>{error}</div>}

      {tab === 'metrics' && (
        <Phase3MetricsTab stats={stats} />
      )}
      {tab === 'reservoir' && (
        <Phase3ReservoirTab entries={reservoir} />
      )}
      {tab === 'drift' && (
        <Phase3DriftTab events={drift} />
      )}
      {tab === 'classifier' && (
        <ClassifierTab
          vehicleId={vehicleId}
          status={classifier}
          onStatusChange={setClassifier}
          onError={setError}
        />
      )}
    </div>
  );
}

function Phase3MetricsTab({ stats }: { stats: MetricStatRow[] | null }) {
  if (stats == null) return <div className="muted">loading…</div>;
  if (stats.length === 0) {
    return (
      <div className="muted" style={{ fontSize: '0.85rem', marginTop: 6 }}>
        No metric stats yet. Run analysis on a few recordings (or
        save-on-reanalyze any existing ones) to bootstrap the
        per-vehicle Welford baselines.
      </div>
    );
  }
  return (
    <>
      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
        Per-vehicle running mean ± std. Bands (mean − 2σ, mean + 2σ)
        feed the trends-modal baseline overlays + the drift detector's
        threshold reference.
      </div>
      <table style={{ fontSize: '0.85rem', marginTop: 6 }}>
        <thead>
          <tr style={{ color: 'var(--muted)' }}>
            <th style={{ textAlign: 'left' }}>metric</th>
            <th style={{ textAlign: 'right' }}>n</th>
            <th style={{ textAlign: 'right' }}>mean</th>
            <th style={{ textAlign: 'right' }}>std</th>
            <th style={{ textAlign: 'right' }}>warn band (mean ± 2σ)</th>
          </tr>
        </thead>
        <tbody>
          {stats.map((s) => (
            <tr key={s.metric_key}>
              <td><code>{s.metric_key}</code></td>
              <td style={{ textAlign: 'right' }}>{s.n}</td>
              <td style={{ textAlign: 'right' }}>{s.mean.toFixed(3)}</td>
              <td style={{ textAlign: 'right' }}>{s.std.toFixed(3)}</td>
              <td style={{ textAlign: 'right' }} className="muted">
                [{s.warn_low.toFixed(2)}, {s.warn_high.toFixed(2)}]
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Phase3ReservoirTab({ entries }: { entries: ReservoirEntry[] | null }) {
  if (entries == null) return <div className="muted">loading…</div>;
  if (entries.length === 0) {
    return (
      <div className="muted" style={{ fontSize: '0.85rem', marginTop: 6 }}>
        Reservoir is empty. Each analyzed recording with a
        cycle-fingerprint enters this reservoir; capacity is 200
        per vehicle. Once full, new recordings are added with
        probability ∝ (1 − cosine sim to the closest existing entry).
      </div>
    );
  }
  return (
    <>
      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
        {entries.length} entries. The reservoir IS the per-cyl SVM
        training set when Phase 4 (Steps N–R) ships — the diversity
        weighting preserves edge examples (rev-ups, post-repair,
        misfire events) instead of letting them get washed out by
        long stretches of similar idle clips.
      </div>
      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6,
                                       maxHeight: 200, overflowY: 'auto' }}>
        {entries.map((e) => (
          <span key={e.id} style={{ marginRight: 12 }}>
            #{e.recording_id} ({new Date(e.added_at).toLocaleDateString()})
          </span>
        ))}
      </div>
    </>
  );
}

function Phase3DriftTab({ events }: { events: DriftEvent[] | null }) {
  if (events == null) return <div className="muted">loading…</div>;
  if (events.length === 0) {
    return (
      <div className="muted" style={{ fontSize: '0.85rem', marginTop: 6 }}>
        No drift events yet. The detector needs at least 15 recordings
        of baseline before it can fire (5-recording recent window vs
        ≥10-recording baseline window). After that, any metric
        whose recent mean strays ≥3σ from baseline triggers an event.
      </div>
    );
  }
  return (
    <>
      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
        {events.length} drift events. Each is a 3σ+ change point
        in one metric stream — likely a real engine-state shift
        (fuel quality, ambient temp swing, post-repair improvement).
      </div>
      <table style={{ fontSize: '0.85rem', marginTop: 6 }}>
        <thead>
          <tr style={{ color: 'var(--muted)' }}>
            <th style={{ textAlign: 'left' }}>detected</th>
            <th style={{ textAlign: 'left' }}>metric</th>
            <th style={{ textAlign: 'right' }}>baseline</th>
            <th style={{ textAlign: 'right' }}>current</th>
            <th style={{ textAlign: 'right' }}>σ</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} title={e.description ?? ''}>
              <td className="muted">{new Date(e.detected_at).toLocaleString()}</td>
              <td><code>{e.metric_key}</code></td>
              <td style={{ textAlign: 'right' }}>{e.baseline_mean?.toFixed(3) ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>{e.current_mean?.toFixed(3) ?? '—'}</td>
              <td style={{ textAlign: 'right', color: 'var(--accent)', fontWeight: 600 }}>
                {e.magnitude_sigmas?.toFixed(2) ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// Step R — Layer-2 SVM admin tab. Three actions:
//   - Train  (POST /classifier/train)  → kicks off Octave + libsvm
//   - Reset  (DELETE /classifier)       → drops the model BLOB so the
//                                          system falls back to Layer-1
//                                          alignment alone
//   - Refresh (re-fetch status)         → after the user adds more
//                                          recordings, the
//                                          reservoir_examples count grows
//
// On a successful train the response includes the same accuracy +
// confusion + gamma fields the GET endpoint returns, so we update the
// status from the train response without a separate fetch.
function ClassifierTab({
  vehicleId,
  status,
  onStatusChange,
  onError,
}: {
  vehicleId: number;
  status: ClassifierStatus | null;
  onStatusChange: (s: ClassifierStatus | null) => void;
  onError: (msg: string | null) => void;
}) {
  const [busy, setBusy] = useState<'train' | 'reset' | null>(null);
  const [trainResult, setTrainResult] = useState<ClassifierTrainResult | null>(null);

  if (status == null) return <div className="muted">loading…</div>;

  async function refresh() {
    onError(null);
    try {
      const fresh = await api.get<ClassifierStatus>(`/api/admin/vehicles/${vehicleId}/classifier`);
      onStatusChange(fresh);
    } catch (e) {
      onError((e as Error).message);
    }
  }

  async function train() {
    setBusy('train');
    setTrainResult(null);
    onError(null);
    try {
      // Train can return ok:false on 422 (training data didn't meet
      // the accuracy/size bar). The api helper rejects on non-2xx;
      // catch the error so we can show its message.
      const result = await api.post<ClassifierTrainResult>(
        `/api/admin/vehicles/${vehicleId}/classifier/train`,
      );
      setTrainResult(result);
      await refresh();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function reset() {
    if (!confirm('Drop the trained model? Future analyses will fall back to Layer-1 alignment until you retrain.')) {
      return;
    }
    setBusy('reset');
    setTrainResult(null);
    onError(null);
    try {
      // The shared api helper has no DELETE wrapper; use fetch
      // directly. Same-origin cookie carries the admin session.
      const r = await fetch(
        `/api/admin/vehicles/${vehicleId}/classifier`,
        { method: 'DELETE', credentials: 'same-origin' },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await refresh();
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const trainable = status.reservoir_examples >= 30 && status.current_slot_dim != null;
  const accuracyPct = status.accuracy != null ? (status.accuracy * 100).toFixed(1) : null;
  const trainedAtStr = status.trained_at != null
    ? new Date(status.trained_at).toLocaleString() : 'never';
  const numClasses = status.num_classes ?? 0;

  return (
    <>
      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
        Per-vehicle multi-class SVM (libsvm RBF, probability estimates)
        that predicts which template-frame slot each recording-frame
        slot belongs to. Trained from the reservoir (Step K) using
        Layer-1 alignment rotations (Step B) as labels. Replaces the
        slot-binning confidence in the user app's per-cyl confidence
        rendering once trained.
      </div>

      <div className="row" style={{ gap: 16, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.85rem' }}>
          <strong>{status.trained ? 'Trained' : 'Not trained'}</strong>
          <div className="muted" style={{ fontSize: '0.78rem' }}>
            last trained: {trainedAtStr}
          </div>
        </div>
        <div style={{ fontSize: '0.85rem' }}>
          <strong>{status.reservoir_examples}</strong>
          <span className="muted"> reservoir examples</span>
          <div className="muted" style={{ fontSize: '0.78rem' }}>
            need ≥ 30 to train (have {status.reservoir_examples})
          </div>
        </div>
        {accuracyPct != null && (
          <div style={{ fontSize: '0.85rem' }}>
            <strong style={{ color: 'var(--accent)' }}>{accuracyPct}%</strong>
            <span className="muted"> held-out accuracy</span>
            <div className="muted" style={{ fontSize: '0.78rem' }}>
              {numClasses}-class (chance is {(100 / Math.max(1, numClasses)).toFixed(0)}%)
            </div>
          </div>
        )}
        {status.support_vectors != null && (
          <div style={{ fontSize: '0.85rem' }}>
            <strong>{status.support_vectors}</strong>
            <span className="muted"> support vectors</span>
            <div className="muted" style={{ fontSize: '0.78rem' }}>
              of {status.n_train ?? '—'} training rows
            </div>
          </div>
        )}
        {status.feature_dim != null && (
          <div style={{ fontSize: '0.85rem' }}>
            <strong>{status.feature_dim}</strong>
            <span className="muted"> feature dim</span>
            <div className="muted" style={{ fontSize: '0.78rem' }}>
              γ = {status.gamma?.toExponential(2) ?? '—'}
            </div>
          </div>
        )}
        <div className="row" style={{ gap: 6, marginLeft: 'auto' }}>
          <button onClick={train} disabled={busy != null || !trainable}
                  title={!trainable ? `Need ≥ 30 reservoir examples (have ${status.reservoir_examples})` : ''}>
            {busy === 'train' ? 'Training…' : status.trained ? 'Retrain' : 'Train'}
          </button>
          {status.trained && (
            <button className="secondary" onClick={reset} disabled={busy != null}>
              Reset
            </button>
          )}
          <button className="secondary" onClick={refresh} disabled={busy != null}>
            Refresh
          </button>
        </div>
      </div>

      {trainResult && (
        <div style={{ marginTop: 8, padding: 8, background: '#0f1730',
                      borderRadius: 6, fontSize: '0.85rem' }}>
          {trainResult.ok ? (
            <span style={{ color: '#4ade80' }}>
              ✓ Trained: {((trainResult.overall_accuracy ?? 0) * 100).toFixed(1)}%
              held-out accuracy on {trainResult.n_test ?? 0} test rows
              ({trainResult.n_train ?? 0} train, {trainResult.support_vectors ?? 0} SVs)
            </span>
          ) : (
            <span style={{ color: '#fb923c' }}>✗ {trainResult.reason ?? 'training refused'}</span>
          )}
        </div>
      )}

      {status.per_class_accuracy && Object.keys(status.per_class_accuracy).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ fontSize: '0.78rem', marginBottom: 4 }}>
            Per-class held-out accuracy. Persistent IDs follow the
            template-frame letters (P-A = class 0, P-B = class 1, …).
            Low per-class numbers point to a slot that's hard to
            distinguish — usually similar fundamental + similar
            harmonic structure to a neighbour.
          </div>
          <table style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ color: 'var(--muted)' }}>
                <th style={{ textAlign: 'left' }}>persistent ID</th>
                <th style={{ textAlign: 'left' }}>class</th>
                <th style={{ textAlign: 'right' }}>accuracy</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(status.per_class_accuracy)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([k, v]) => {
                  const idx = parseInt(k.replace('cls_', ''), 10);
                  const persistentId = `P-${String.fromCharCode(65 + idx)}`;
                  const pct = v >= 0 ? `${(v * 100).toFixed(1)}%` : '—';
                  const colour = v < 0 ? 'var(--muted)'
                              : v >= 0.85 ? '#4ade80'
                              : v >= 0.6 ? '#facc15'
                              : '#f87171';
                  return (
                    <tr key={k}>
                      <td><strong>{persistentId}</strong></td>
                      <td className="muted">{k}</td>
                      <td style={{ textAlign: 'right', color: colour, fontWeight: 600 }}>
                        {pct}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {status.confusion && status.confusion.length > 0 && (
        <ConfusionMatrix confusion={status.confusion} />
      )}
    </>
  );
}

// Square heatmap-style table. Rows are the true template_slot, cols
// are the predicted template_slot. Diagonal is correct, off-diagonal
// cells colour by count to make systematic confusions visible — e.g.
// if P-B is consistently mispredicted as P-D, that pair will glow.
function ConfusionMatrix({ confusion }: { confusion: number[][] }) {
  const N = confusion.length;
  if (N === 0) return null;
  let maxOff = 0;
  let total = 0;
  for (let i = 0; i < N; i += 1) {
    for (let j = 0; j < N; j += 1) {
      total += confusion[i][j];
      if (i !== j && confusion[i][j] > maxOff) maxOff = confusion[i][j];
    }
  }
  const id = (i: number) => `P-${String.fromCharCode(65 + i)}`;
  const cellBg = (i: number, j: number, v: number) => {
    if (v === 0) return 'transparent';
    if (i === j) return `rgba(74, 222, 128, ${0.15 + 0.6 * (v / Math.max(1, total / N))})`;
    if (maxOff === 0) return 'transparent';
    return `rgba(248, 113, 113, ${0.15 + 0.6 * (v / maxOff)})`;
  };
  return (
    <div style={{ marginTop: 12 }}>
      <div className="muted" style={{ fontSize: '0.78rem', marginBottom: 4 }}>
        Confusion matrix (held-out set). Rows = true class, columns =
        predicted. Green diagonal cells = correct; red off-diagonal
        cells = systematic confusion between two persistent IDs.
      </div>
      <table style={{ fontSize: '0.78rem', borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr>
            <th></th>
            <th colSpan={N} style={{ textAlign: 'center', color: 'var(--muted)' }}>predicted</th>
          </tr>
          <tr>
            <th></th>
            {Array.from({ length: N }, (_, j) => (
              <th key={j} style={{ padding: '2px 6px', color: 'var(--muted)' }}>{id(j)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {confusion.map((row, i) => (
            <tr key={i}>
              <th style={{ padding: '2px 6px', color: 'var(--muted)', textAlign: 'right' }}>
                {id(i)}
              </th>
              {row.map((v, j) => (
                <td key={j} style={{
                  padding: '4px 8px', textAlign: 'center',
                  border: '1px solid #1a233f',
                  background: cellBg(i, j, v),
                  fontWeight: i === j ? 600 : 400,
                  minWidth: 32,
                }}>
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
